import { Building2Icon, CheckIcon, SearchIcon, UsersIcon } from 'lucide-react'
import React, { useId, useMemo, useState } from 'react'
import { toast } from 'sonner'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { useEmployerSwitchSync } from 'hooks/useEmployerSwitchSync'
import { employersEmployees, membersDashboard, partnersClients } from 'rails_routes'
import { TextInput } from 'slab'
import { type MenuContext } from 'types'
import { capitalize } from 'utils'
import { useGetUsersOrganizations, usePostUsersOrganizationsSetCurrent } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

type OrganizationType = 'member' | 'employer' | 'partner'
interface OrganizationItem {
  id: string
  name: string
  displayName: string
  type: OrganizationType
  subtitle: string
  isCurrent: boolean
  icon: React.ComponentType<{ className?: string }>
  initials?: string
  path: string
}

interface OrganizationSwitcherProps {
  currentOrganizationId: string
  currentMenuContext: MenuContext
}

const ErrorComponent = () => {
  const menuId = useId()
  const errorGradientId = useId()

  return (
    <div id={menuId} className='rounded-lg bg-white shadow-lg border border-gray-200 focus:outline-none max-h-[60vh] flex flex-col'>
      <div className='p-4 text-gray-500 body-sm text-center'>
        <div className='mb-2'>
          <svg xmlns='http://www.w3.org/2000/svg' width='248' height='117' fill='none' viewBox='0 0 248 117'><rect width='248' height='117' fill='#fafafa' rx='16' /><rect width='48' height='48' x='52' y='44.42' fill={`url(#${errorGradientId}-a)`} rx='24' transform='rotate(-15 52 44.42)' /><path fill={`url(#${errorGradientId}-b)`} d='M135.39 35.86a4 4 0 0 1 4.9-2.82l38.63 10.35a4 4 0 0 1 2.83 4.9L171.4 86.92a4 4 0 0 1-4.9 2.83L127.86 79.4a4 4 0 0 1-2.83-4.9z' /><rect width='48' height='48' x='92' y='27' fill={`url(#${errorGradientId}-c)`} rx='4' /><path fill='#ec3939' fillRule='evenodd' d='M124.94 42.06c.52.52.52 1.36 0 1.88l-16 16a1.33 1.33 0 0 1-1.88-1.88l16-16a1.33 1.33 0 0 1 1.88 0' clipRule='evenodd' /><path fill='#ec3939' fillRule='evenodd' d='M107.06 42.06a1.33 1.33 0 0 1 1.88 0l16 16a1.33 1.33 0 0 1-1.88 1.88l-16-16a1.33 1.33 0 0 1 0-1.88' clipRule='evenodd' /><defs><linearGradient id={`${errorGradientId}-a`} x1='76' x2='76' y1='44.42' y2='92.42' gradientUnits='userSpaceOnUse'><stop stopColor='#f98f8f' /><stop offset='1' stopColor='#fbdede' /></linearGradient><linearGradient id={`${errorGradientId}-b`} x1='159.61' x2='147.18' y1='38.21' y2='84.58' gradientUnits='userSpaceOnUse'><stop stopColor='#feeef0' /><stop offset='1' stopColor='#ffc0c0' /></linearGradient><linearGradient id={`${errorGradientId}-c`} x1='116' x2='116' y1='27' y2='75' gradientUnits='userSpaceOnUse'><stop stopColor='#fbdede' /><stop offset='1' stopColor='#f98f8f' /></linearGradient></defs></svg>
        </div>
        <p>There was an error loading your accounts.</p>
        <p>Please refresh the page and try again.</p>
      </div>
    </div>
  )
}

const NoResultsComponent = () => {
  const missingGradientId = useId()

  return (
    <div className='p-4 text-gray-500 body-sm text-center'>
      <div className='mb-2'>
        <svg xmlns='http://www.w3.org/2000/svg' width='248' height='117' fill='none' viewBox='0 0 248 117'><rect width='248' height='117' fill='#fafafa' rx='16' /><rect width='48' height='48' x='52' y='44.42' fill={`url(#${missingGradientId}-a)`} rx='24' transform='rotate(-15 52 44.42)' /><path fill='#4b1a7d' fillRule='evenodd' d='M80.35 56.18a2.7 2.7 0 0 0-2.41 2.46 1.33 1.33 0 1 1-2.66-.2 5.33 5.33 0 0 1 10.47-.97c.53 1.97-.58 3.66-1.44 4.64a11 11 0 0 1-1.88 1.67l-.04.03h-.01v.01l-.74-1.11.73 1.11a1.33 1.33 0 0 1-1.47-2.22l.02-.02.42-.31q.44-.35.97-.92 1.1-1.27.87-2.2a2.7 2.7 0 0 0-2.83-1.97M81.83 68.18c-.19-.71.23-1.44.94-1.63h.02a1.33 1.33 0 0 1 .69 2.57h-.02c-.7.2-1.44-.23-1.63-.94' clipRule='evenodd' /><rect width='48' height='48' x='136.42' y='32' fill={`url(#${missingGradientId}-b)`} rx='4' transform='rotate(15 136.42 32)' /><path fill='#4b1a7d' fillRule='evenodd' d='M155.1 56.36a2.7 2.7 0 0 0-3.32.92 1.33 1.33 0 0 1-2.2-1.5 5.33 5.33 0 0 1 9.55 4.4c-.53 1.96-2.34 2.87-3.57 3.29a11 11 0 0 1-2.46.51h-.07l-.08-1.33.08 1.34a1.33 1.33 0 0 1-.17-2.67h.03l.52-.06c.35-.06.82-.16 1.3-.32q1.6-.54 1.85-1.46a2.7 2.7 0 0 0-1.46-3.12M150.38 67.49c.2-.71.92-1.14 1.63-.94h.02a1.33 1.33 0 1 1-.7 2.57 1.33 1.33 0 0 1-.95-1.63' clipRule='evenodd' /><rect width='48' height='48' x='92' y='27' fill={`url(#${missingGradientId}-c)`} rx='4' /><path fill='#4b1a7d' fillRule='evenodd' d='M116.34 45.7a2.7 2.7 0 0 0-2.96 1.74 1.33 1.33 0 0 1-2.52-.88 5.33 5.33 0 0 1 10.37 1.77c0 2.05-1.52 3.4-2.6 4.11a11 11 0 0 1-2.24 1.13l-.05.02h-.02l-.43-1.26.42 1.27a1.33 1.33 0 0 1-.84-2.53h.02l.1-.05a8 8 0 0 0 1.56-.8c.92-.6 1.41-1.26 1.41-1.89a2.66 2.66 0 0 0-2.22-2.63M114.67 57.67c0-.74.6-1.34 1.33-1.34h.01a1.33 1.33 0 1 1 0 2.67H116c-.74 0-1.33-.6-1.33-1.33' clipRule='evenodd' /><defs><linearGradient id={`${missingGradientId}-a`} x1='76' x2='76' y1='44.42' y2='92.42' gradientUnits='userSpaceOnUse'><stop stopColor='#dbb6ff' /><stop offset='1' stopColor='#f1e2ff' /></linearGradient><linearGradient id={`${missingGradientId}-b`} x1='160.42' x2='160.42' y1='32' y2='80' gradientUnits='userSpaceOnUse'><stop stopColor='#e5e5e5' /><stop offset='1' stopColor='#cfcfcf' /></linearGradient><linearGradient id={`${missingGradientId}-c`} x1='116' x2='116' y1='27' y2='75' gradientUnits='userSpaceOnUse'><stop stopColor='#f1e2ff' /><stop offset='1' stopColor='#dbb6ff' /></linearGradient></defs></svg>
      </div>
      <p>We didn't find any accounts by that name.</p>
      <p>If you believe there's been a mistake, reach out to your administrator.</p>
    </div>
  )
}

const OrganizationSwitcher = ({ currentOrganizationId, currentMenuContext }: OrganizationSwitcherProps) => {
  const { data: organizations, isLoading, error } = useGetUsersOrganizations()
  const switchContextMutation = usePostUsersOrganizationsSetCurrent()
  const { notifyOtherTabs } = useEmployerSwitchSync(currentMenuContext)
  const [searchQuery, setSearchQuery] = useState('')
  const { assign } = useWindowLocation()
  const menuId = useId()

  const generateInitials = (name: string): string => {
    return name.replace(/[^a-zA-Z ]/g, '').split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
  }

  // Transform organizations data into a flat list of items
  const organizationItems = useMemo((): OrganizationItem[] => {
    if (!organizations) return []

    const items: OrganizationItem[] = []

    organizations.members.forEach((member) => {
      items.push({
        id: member.employer.id, // Use employer ID instead of member ID
        name: member.employer.name,
        displayName: member.employer.display_name,
        type: 'member' as OrganizationType,
        subtitle: 'Member',
        isCurrent: member.employer.id === currentOrganizationId && currentMenuContext === 'member' as MenuContext,
        icon: UsersIcon,
        initials: generateInitials(member.employer.name),
        path: membersDashboard.index()
      })
    })

    organizations.employer_admins.forEach((admin) => {
      items.push({
        id: admin.employer.id,
        name: admin.employer.name,
        displayName: admin.employer.display_name,
        type: 'employer' as OrganizationType,
        subtitle: admin.partner_name ? admin.partner_name : '', // We don't render a subtitle for regular employer admins
        isCurrent: admin.employer.id === currentOrganizationId && currentMenuContext === 'employer' as MenuContext,
        icon: Building2Icon,
        initials: generateInitials(admin.employer.name),
        path: employersEmployees.index()
      })
    })

    organizations.partner_admins.forEach((admin) => {
      const name = admin.partner.friendly_name || admin.partner.display_name

      items.push({
        id: admin.partner.id,
        name,
        displayName: name,
        type: 'partner' as OrganizationType,
        subtitle: admin.roles.includes('admin') ? 'Admin' : admin.roles.map(role => capitalize(role)).join(', '),
        isCurrent: admin.partner.id === currentOrganizationId,
        icon: Building2Icon,
        initials: generateInitials(name),
        path: partnersClients.index()
      })
    })

    return items
  }, [organizations, currentOrganizationId, currentMenuContext])

  // Filter organizations based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return organizationItems

    const query = searchQuery.toLowerCase()
    return organizationItems.filter(item =>
      item.name.toLowerCase().includes(query) ||
      item.subtitle.toLowerCase().includes(query) ||
      item.displayName.toLowerCase().includes(query)
    )
  }, [organizationItems, searchQuery])

  // Group organizations according to the specified logic
  const groupedOrganizations = useMemo(() => {
    const groups: {
      yourAccounts: OrganizationItem[]
      partnerAccounts: OrganizationItem[]
      employerAdminAccounts: {
        grouped: { [name: string]: OrganizationItem[] }
        other: OrganizationItem[]
      }
    } = {
      yourAccounts: [],
      partnerAccounts: [],
      employerAdminAccounts: {
        grouped: {},
        other: []
      }
    }

    // Get member employer IDs for grouping logic
    const memberEmployerIds = new Set(
      organizations?.members.map(member => member.employer.id) || []
    )

    filteredItems.forEach(item => {
      switch (item.type) {
        case 'member' as OrganizationType:
          groups.yourAccounts.push(item)
          break
        case 'employer' as OrganizationType:
          // If user has a member for the same employer, show under "your accounts"
          if (memberEmployerIds.has(item.id)) {
            groups.yourAccounts.push(item)
          } else {
            // Group employer admins by displayname
            const employerDisplayName = item.displayName
            if (!groups.employerAdminAccounts.grouped[employerDisplayName]) {
              groups.employerAdminAccounts.grouped[employerDisplayName] = []
            }
            groups.employerAdminAccounts.grouped[employerDisplayName].push(item)
          }
          break
        case 'partner' as OrganizationType:
          groups.partnerAccounts.push(item)
          break
      }
    })

    // Move single-item groups to "other" category
    Object.entries(groups.employerAdminAccounts.grouped).forEach(([name, items]) => {
      if (items.length === 1) {
        groups.employerAdminAccounts.other.push(...items)
        delete groups.employerAdminAccounts.grouped[name]
      }
    })

    // Sort everything alphabetically
    Object.values(groups.employerAdminAccounts.grouped).forEach(items => {
      items.sort((a, b) => a.name.localeCompare(b.name))
    })
    groups.employerAdminAccounts.other.sort((a, b) => a.name.localeCompare(b.name))
    groups.yourAccounts.sort((a, b) => a.name.localeCompare(b.name))
    groups.partnerAccounts.sort((a, b) => a.name.localeCompare(b.name))

    return groups
  }, [filteredItems, organizations])

  const handleOrganizationSelect = (item: OrganizationItem, event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (item.isCurrent) return
    if (switchContextMutation.isPending) return

    switchContextMutation.mutate({
      data: {
        organization_type: item.type,
        organization_id: item.id
      }
    }, {
      onSuccess: () => {
      // If we're switching to an employer, we need to notify other tabs
        if (item.type === 'employer' as OrganizationType) {
          notifyOtherTabs(item.id)
        }

        document.dispatchEvent(new Event('close-sidebar'))

        assign(item.path)
      },
      onError: (error) => {
        console.error('Failed to switch organization:', error)
        toast.error(
          'Failed to switch organization. If this issue persists, please contact support.'
        )
      }
    })
  }

  const renderOrganizationItem = (item: OrganizationItem, renderIcon = false) => {
    const Icon = item.icon

    let iconBackgroundClass = 'bg-gradient-to-b rounded '
    if (item.type === 'member' as OrganizationType) {
      iconBackgroundClass += 'bg-gradient-to-t rounded-full '
    }

    if (item.type === 'member' as OrganizationType || (item.type === 'employer' as OrganizationType && item.subtitle === '')) {
      if (item.isCurrent) {
        iconBackgroundClass += 'from-thatch-300 to-thatch-400'
      } else {
        iconBackgroundClass += 'from-thatch-200 to-thatch-300 group-hover:from-thatch-300 group-hover:to-thatch-400'
      }
    } else {
      if (item.isCurrent) {
        iconBackgroundClass += 'from-gray-300 to-gray-400'
      } else {
        iconBackgroundClass += 'from-gray-200 to-gray-300 group-hover:from-gray-300 group-hover:to-gray-400'
      }
    }

    let subtitle = item.subtitle
    // If we are rendering an icon, we have a member - differentiate the member/employer
    // by showing the admin subtitle on the ER
    if (renderIcon && item.type === 'employer' as OrganizationType) {
      subtitle = 'Admin'
    }

    // If it was successful, we're waiting for the page to reload so disable
    const isDisabled = switchContextMutation.isPending || switchContextMutation.isSuccess

    return (
      <a
        className={`${item.isCurrent ? 'bg-gray-100' : ''} ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'} flex group rounded-lg items-center p-2 text-gray-800 text-left focus-visible:bg-gray-100 outline outline-2 outline-transparent focus-visible:outline-thatch-500`}
        onClick={(event) => !isDisabled && handleOrganizationSelect(item, event)}
        href={item.path}
      >
        <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 mr-2 ${iconBackgroundClass}`}>
          <span className='body-sm-bold text-gray-600'>
            {renderIcon
              ? (
                <Icon className='w-4 h-4 text-gray-600' />
                )
              : (
                  item.initials
                )}
          </span>
        </div>
        <div className='flex flex-col flex-grow min-w-0 h-10'>
          <div className={`body-bold text-gray-800 my-auto ${subtitle ? 'truncate' : 'line-clamp-2'}`} title={item.name}>
            {item.name}
          </div>
          {subtitle && (
            <div className='body-sm text-gray-500 truncate'>
              {subtitle}
            </div>
          )}
        </div>
        {item.isCurrent && (
          <CheckIcon className='w-4 h-4 text-thatch-500 flex-shrink-0' data-testid='current-organization-icon' />
        )}
      </a>
    )
  }

  const renderGroup = (title: string, items: OrganizationItem[]) => {
    if (items.length === 0) return null

    // Only render the icon for the "Primary accounts" group
    const renderIcon = title === 'Primary accounts' && items.length > 1

    return (
      <div>
        <div className='p-4 pb-2 flex gap-3 items-center'>
          <p className='body-sm-bold text-gray-500'>
            {title}
          </p>
          <hr className='border-gray-200 flex-grow' />
        </div>
        <div className='p-2 space-y-1'>
          {items.map(item => (
            <React.Fragment key={`${item.type}-${item.id}`}>
              {renderOrganizationItem(item, renderIcon)}
            </React.Fragment>
          ))}
        </div>
      </div>
    )
  }

  const renderEmployerAdminGroup = (grouped: { [name: string]: OrganizationItem[] }, other: OrganizationItem[]) => {
    const hasGroupedItems = Object.keys(grouped).length > 0
    const hasOtherItems = other.length > 0

    if (!hasGroupedItems && !hasOtherItems) return null

    return (
      <div>
        <div className='p-4 pb-2 flex gap-3 items-center'>
          <p className='body-sm-bold text-gray-500'>
            Your employer accounts
          </p>
          <hr className='border-gray-200 flex-grow' />
        </div>
        <div className='p-2'>
          {Object.entries(grouped).map(([name, items]) => (
            <div key={name}>
              <div className='px-2 pb-1'>
                <p className='body-sm text-gray-500'>
                  {name}
                </p>
              </div>
              <div className='py-2 space-y-1'>
                {items.map(item => (
                  <React.Fragment key={`${item.type}-${item.id}`}>
                    {renderOrganizationItem(item)}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
          {hasOtherItems &&
            <div key='other'>
              {hasGroupedItems &&
                <div className='p-2'>
                  <p className='body-sm text-gray-500'>
                    Other accounts
                  </p>
                </div>}
              <div className='space-y-1'>
                {other.map(item => (
                  <React.Fragment key={`${item.type}-${item.id}`}>
                    {renderOrganizationItem(item)}
                  </React.Fragment>
                ))}
              </div>
            </div>}
        </div>
      </div>
    )
  }

  if (error) {
    return <ErrorComponent />
  }

  return (
    <div id={menuId} className='rounded-lg bg-white shadow-lg border border-gray-200 focus:outline-none max-h-[60vh] overflow-y-auto flex flex-col'>
      {/* Only show search input if there are more than 5 organizations */}
      {organizationItems.length > 5 && (
        <div className='flex flex-col relative w-full p-4 gap-2'>
          <TextInput
            placeholder='Find an account'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leadingIcon={SearchIcon}
          />
          {filteredItems.length > 0 && searchQuery && (
            <div className='body-sm'>
              {filteredItems.length} {filteredItems.length === 1 ? 'account' : 'accounts'}
            </div>
          )}
        </div>
      )}

      {/* Organization Groups */}
      {isLoading
        ? (
          <div className='py-8'>
            <LoadingSpinnerCentered />
          </div>
          )
        : (
          <>
            {renderGroup('Primary accounts', groupedOrganizations.yourAccounts)}
            {renderGroup('Your partner accounts', groupedOrganizations.partnerAccounts)}
            {renderEmployerAdminGroup(groupedOrganizations.employerAdminAccounts.grouped, groupedOrganizations.employerAdminAccounts.other)}

            {filteredItems.length === 0 && searchQuery && (
              <NoResultsComponent />
            )}
          </>
          )}
    </div>
  )
}

export default OrganizationSwitcher
